import { useMemo, type ReactElement } from 'react'
import { getMillisFromDateValue } from '@util'
import { type QuestionId } from '@terros/common'
import { useNavigate, useParams, createFileRoute } from '@tanstack/react-router'
import { useToastError } from '@hooks'
import { useQuestion, useTranslation, useUpdateQuestion } from '@front/model'
import { ButtonSave, NotReady, Page, QuestionForm, useQuestionForm, type QuestionFormOutput } from '@components'

function QuestionEditPage(): ReactElement {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { questionId } = useParams({ strict: false }) as { questionId: QuestionId }
  const { data: question, isLoading: isLoadingQuestion, error: loadError } = useQuestion(questionId!)
  const { update, isUpdating, error } = useUpdateQuestion()
  useToastError(error)
  useToastError(loadError)

  const form = useQuestionForm(question)
  const disabled = useMemo((): boolean => {
    const startMillis = getMillisFromDateValue(form.values.startDate)
    if (!startMillis) return false
    return Date.now() >= startMillis
  }, [form.values.startDate])

  if (isLoadingQuestion) return <NotReady id='Question' isLoading={isLoadingQuestion} />
  if (loadError || !question) return <NotReady data={question} error={loadError} id='Question' />

  const handleSubmit = async (values: QuestionFormOutput): Promise<void> => {
    await update({
      question: {
        ...question,
        ...values,
      },
    })
    navigate({ to: `/questions/${questionId}` })
  }

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Page>
        <Page.Heading back title={t('Edit Startup Question')}>
          <ButtonSave loading={isUpdating} type='submit' disabled={!form.isDirty() || !form.isValid()}>
            {t('Save')}
          </ButtonSave>
        </Page.Heading>
        <Page.Content scrollArea>
          <QuestionForm form={form} disabled={disabled} questionId={questionId} />
        </Page.Content>
      </Page>
    </form>
  )
}

export const Route = createFileRoute('/questions_/$questionId_/edit/')({
  component: QuestionEditPage,
})
